import { useEffect, useState } from 'react';
import { useMap } from 'react-leaflet';

/**
 * Guards shared by every map layer that reacts to raw user input on the map
 * surface (click-to-place, long-press-to-drop). Extracted from
 * `LocationMarker`, which solved both problems first; `LongPressPin` needs
 * exactly the same protection, and two copies would drift.
 */

/** Leaflet elements that own their own clicks — never treat these as "the map". */
const CHROME_SELECTORS = [
  '.leaflet-draw-toolbar',
  '.leaflet-draw-actions',
  '.leaflet-marker-icon',
  '.leaflet-interactive',
  '.leaflet-control',
  '.leaflet-popup',
] as const;

/**
 * True when the event landed on Leaflet chrome or an existing overlay rather
 * than bare map — a draw-toolbar button, another marker, a drawn shape, an
 * open popup. Without this the pin jumps while the user is aiming at a control.
 */
export function isLeafletChromeTarget(target: EventTarget | null | undefined): boolean {
  const el = target as HTMLElement | undefined;
  if (!el || typeof el.closest !== 'function') return false;
  if (CHROME_SELECTORS.some((sel) => el.closest(sel))) return true;
  return (
    el.classList?.contains('leaflet-marker-icon') === true ||
    el.classList?.contains('leaflet-interactive') === true
  );
}

/**
 * Tracks whether leaflet-draw currently owns the map.
 *
 * Listens to the full draw lifecycle (start/stop/vertex/cancel/edit/delete)
 * so a half-finished shape doesn't leave us in the wrong state, plus a slow
 * poll of the toolbar's active-button class to cover the race where the
 * polygon tool is enabled before any draw event fires.
 *
 * Resolves the map via `useMap`, so it must be called inside a MapContainer
 * subtree. Taking the map as an argument would tempt callers to pass a ref's
 * current value, which is null on first render and never re-renders — the
 * listeners would silently never bind.
 */
export function useIsDrawingMode(): boolean {
  const map = useMap();
  const [isDrawing, setIsDrawing] = useState(false);

  useEffect(() => {
    if (!map) return undefined;

    const onStart = () => setIsDrawing(true);
    const onStop = () => setIsDrawing(false);

    const events: Array<[string, () => void]> = [
      ['draw:drawstart', onStart],
      ['draw:drawstop', onStop],
      ['draw:created', onStop],
      ['draw:canceled', onStop],
      ['draw:drawvertex', onStart],
      ['draw:editstart', onStart],
      ['draw:editstop', onStop],
      ['draw:deletestart', onStart],
      ['draw:deletestop', onStop],
    ];

    events.forEach(([name, handler]) => map.on(name, handler));
    return () => {
      events.forEach(([name, handler]) => map.off(name, handler));
    };
  }, [map]);

  // Safety net for the mount race described above.
  useEffect(() => {
    if (!map) return undefined;
    const id = window.setInterval(() => {
      const toolbar = document.querySelector('.leaflet-draw-toolbar');
      if (!toolbar) return;
      const polygonBtn = toolbar.querySelector('.leaflet-draw-draw-polygon');
      if (polygonBtn?.classList.contains('leaflet-draw-toolbar-button-enabled')) {
        setIsDrawing(true);
      }
    }, 500);
    return () => window.clearInterval(id);
  }, [map]);

  return isDrawing;
}
